/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {withErrorBoundary} from '@ion/dls/src/hoc/withErrorBoundary';

// defining the props definitions...
export interface WithPageErrorBoundaryProps {
  onErrorRecoveryAction?: Function;
};

/**
 * The `withPageErrorBoundary` higher-order component (HOC) allows for enhancing
 * a page level component (next.js page component) with page scoped error handling
 * capabilities.
 *
 * This HOC catches unhandled errors and displays an error message to the user
 * while providing an option for error recovery.
 *
 * ```typescript
 *  // define the page component (i.e. /pages/items.tsx)
 *  const Page = ({tReady}: LocalizedPageProps) => {
 *    const {isReady} = usePageReady({tReady});
 *    return (
 *      <>
 *        <PageTitle namespace={'items'} initialized={tReady} />
 *        {isReady ? <Item /> : <ContentLoader />}
 *      </>
 *    );
 *  };
 *
 *  // wrap page component with error-handling functionality and export ...
 *  const ItemsPage = withTranslation([
 *   'items'
 *  ])(withPageErrorBoundary(Page, {
 *    onErrorRecoveryAction : reInitializeItems
 *  })) as PageLayoutConsumer;
 *
 * ```
 *
 * @param   Component The component that is to be enhanced with error handling
 *          capabilities.
 * @param   onErrorRecoveryAction An optional action creator function to be
 *          dispatched when the user requests a page refresh.
 * @returns A new React component which wraps the provided Page Component with
 *          error-handling capabilities.
 */
export const withPageErrorBoundary = (Component: React.ComponentType, {
  onErrorRecoveryAction
}: WithPageErrorBoundaryProps = {}) => {
  return (props) => {
    const dispatch = useDispatch();

    const onErrorRecovery = useCallback(() => {
      if (onErrorRecoveryAction) {
        dispatch(onErrorRecoveryAction());
      }
    }, [onErrorRecoveryAction]);

    const WrappedComponent = useMemo(() => (
      withErrorBoundary(Component, {
        omitRefresh : !onErrorRecoveryAction,
        onErrorRecovery
      })), [onErrorRecovery]);

    return <WrappedComponent {...props} />;
  };
};

export default withPageErrorBoundary;
